import { z } from 'zod';

import {
  RM_AUTO_REPLY_DELAY,
  RM_AUTO_REPLY_REVIEW_RESTRICTION,
  RM_BREAKDOWN_INTERVAL,
  RM_BREAKDOWN_TYPE,
  RM_EMAIL_ALERT_MODE,
  RM_PROFILE_LOOKUP_STATUS,
  RM_RATING_BUCKET,
  RM_REVIEW_GRADE,
  RM_REVIEW_RATING_FILTER,
  RM_REVIEW_STATUS,
  RM_REVIEW_TYPE,
  RM_STAR_RATING,
  RM_TEMPLATE_RATING_TYPE
} from '../dictionary';

/** Id of a Reputation Manager report. */
export const RmReportIdSchema = z.number();
/** Id of a Reputation Manager report. */
export type RmReportId = z.infer<typeof RmReportIdSchema>;

/** Id (UUID) of a Reputation Manager profile lookup. */
export const RmProfileLookupIdSchema = z.string();
/** Id (UUID) of a Reputation Manager profile lookup. */
export type RmProfileLookupId = z.infer<typeof RmProfileLookupIdSchema>;

/** Zod schema validating the status of a Reputation Manager profile lookup. */
export const RmProfileLookupStatusSchema = z.nativeEnum(RM_PROFILE_LOOKUP_STATUS);
/** `running` until every requested directory is searched, then `completed`, or `failed` when it stopped early. */
export type RmProfileLookupStatus = z.infer<typeof RmProfileLookupStatusSchema>;

/** Identifier of a review site (directory) in Reputation Manager, e.g. `google` or `yelp`. */
export const RmDirectoryIdentifierSchema = z.string();
/** Identifier of a review site (directory) in Reputation Manager, e.g. `google` or `yelp`. */
export type RmDirectoryIdentifier = z.infer<typeof RmDirectoryIdentifierSchema>;

/** Zod schema validating a Reputation Manager rating bucket. */
export const RmRatingBucketSchema = z.nativeEnum(RM_RATING_BUCKET);
/** Rating bucket of a review: `5`–`1` as strings, `recommended` or `not_recommended`. */
export type RmRatingBucket = z.infer<typeof RmRatingBucketSchema>;

/** Zod schema validating a Reputation Manager directory breakdown interval. */
export const RmBreakdownIntervalSchema = z.nativeEnum(RM_BREAKDOWN_INTERVAL);
/** Calendar period the directory breakdown is bucketed by. */
export type RmBreakdownInterval = z.infer<typeof RmBreakdownIntervalSchema>;

/** Zod schema validating a Reputation Manager directory breakdown counting mode. */
export const RmBreakdownTypeSchema = z.nativeEnum(RM_BREAKDOWN_TYPE);
/** `all` counts a running total up to each period, `new` the reviews left in that period alone. */
export type RmBreakdownType = z.infer<typeof RmBreakdownTypeSchema>;

/** Zod schema validating a Reputation Manager email alert mode. */
export const RmEmailAlertModeSchema = z.nativeEnum(RM_EMAIL_ALERT_MODE);
/** When a Reputation Manager report sends email alerts. */
export type RmEmailAlertMode = z.infer<typeof RmEmailAlertModeSchema>;

/** Id of a Reputation Manager review. */
export const RmReviewIdSchema = z.number();
/** Id of a Reputation Manager review. */
export type RmReviewId = z.infer<typeof RmReviewIdSchema>;

/** Zod schema validating the workflow status of a Reputation Manager review. */
export const RmReviewStatusSchema = z.nativeEnum(RM_REVIEW_STATUS);
/** Workflow status of a review in the review inbox. */
export type RmReviewStatus = z.infer<typeof RmReviewStatusSchema>;

/** Zod schema validating the kind of a Reputation Manager review. */
export const RmReviewTypeSchema = z.nativeEnum(RM_REVIEW_TYPE);
/** `review` carries a star rating, `recommendation` a grade. */
export type RmReviewType = z.infer<typeof RmReviewTypeSchema>;

/** Zod schema validating the grade of a Reputation Manager recommendation. */
export const RmReviewGradeSchema = z.nativeEnum(RM_REVIEW_GRADE);
/** Grade of a review whose type is `recommendation`. */
export type RmReviewGrade = z.infer<typeof RmReviewGradeSchema>;

/** Zod schema validating a Find Reviews rating filter value. */
export const RmReviewRatingFilterSchema = z.nativeEnum(RM_REVIEW_RATING_FILTER);
/** `1`–`5` as integers, `recommended`, `not_recommended` or `no_rating`. */
export type RmReviewRatingFilter = z.infer<typeof RmReviewRatingFilterSchema>;

/** Zod schema validating a Reputation Manager star rating. */
export const RmStarRatingSchema = z.nativeEnum(RM_STAR_RATING);
/** `1`–`5` as integers. */
export type RmStarRating = z.infer<typeof RmStarRatingSchema>;

/** Id of a Reputation Manager reply template. */
export const RmReplyTemplateIdSchema = z.number();
/** Id of a Reputation Manager reply template. */
export type RmReplyTemplateId = z.infer<typeof RmReplyTemplateIdSchema>;

/** Zod schema validating the ratings a Reputation Manager reply template is offered for. */
export const RmTemplateRatingTypeSchema = z.nativeEnum(RM_TEMPLATE_RATING_TYPE);
/** `all_ratings`, `1`–`5` as integers, `recommended` or `not_recommended`. */
export type RmTemplateRatingType = z.infer<typeof RmTemplateRatingTypeSchema>;

/** Id of a Reputation Manager auto-reply rule. */
export const RmAutoReplyRuleIdSchema = z.number();
/** Id of a Reputation Manager auto-reply rule. */
export type RmAutoReplyRuleId = z.infer<typeof RmAutoReplyRuleIdSchema>;

/** Zod schema validating the delay of an auto-reply rule, in minutes. */
export const RmAutoReplyDelaySchema = z.nativeEnum(RM_AUTO_REPLY_DELAY);
/** `60`, `480`, `1440` or `2880` minutes. */
export type RmAutoReplyDelay = z.infer<typeof RmAutoReplyDelaySchema>;

/** Zod schema validating which reviews an auto-reply rule replies to. */
export const RmAutoReplyReviewRestrictionSchema = z.nativeEnum(RM_AUTO_REPLY_REVIEW_RESTRICTION);
/** `with_text` or `without_text`. */
export type RmAutoReplyReviewRestriction = z.infer<typeof RmAutoReplyReviewRestrictionSchema>;
